import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';

import { humanizeKey } from '../utils/humanizeKey.js';

/**
 * The line above a binding control: what the slot is called, whether the type
 * requires it, and whether the builder can author it at all (fix/widgets).
 *
 * The name is looked up by key **and role** first — a bar chart's `y` is the
 * measure, a heatmap's is the second axis — and falls back to the key's own
 * name and then to a humanised key, so a slot the catalog adds tomorrow reads
 * sensibly without a translation.
 *
 * `unsupported` is the point of this component. A slot the builder cannot
 * author yet is **shown with a red mark** rather than left out: the field is
 * in the contract, the author should know it exists, and a gap that is
 * visible is a gap someone can ask about.
 */
function BindingSlotLabel({ slotKey, role, required, unsupported, auto }) {
  const { t } = useTranslation('dashboards');

  const label = t(`widgetBuilder.bindingSlots.names.${slotKey}__${role}`, {
    defaultValue: t(`widgetBuilder.bindingSlots.names.${slotKey}`, {
      defaultValue: humanizeKey(slotKey),
    }),
  });

  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
      <span className="truncate text-xs font-medium text-content-primary">{label}</span>
      <span className="shrink-0 text-[11px] text-content-muted">
        {required
          ? t('widgetBuilder.bindingSlots.required')
          : t('widgetBuilder.bindingSlots.optional')}
      </span>
      {auto ? (
        <span className="shrink-0 text-[11px] text-content-muted">
          {t('widgetBuilder.bindingSlots.automatic')}
        </span>
      ) : null}
      {unsupported ? (
        <span className="shrink-0 text-[11px] font-medium text-red-400">
          {t('widgetBuilder.bindingSlots.unsupportedTag')}
        </span>
      ) : null}
    </div>
  );
}

BindingSlotLabel.propTypes = {
  slotKey: PropTypes.string.isRequired,
  /** `measure` / `dimension` / `column`, for the role-specific name. */
  role: PropTypes.string,
  required: PropTypes.bool,
  /** The builder has no editor for this slot yet. */
  unsupported: PropTypes.bool,
  /** Filled without asking — the record key. */
  auto: PropTypes.bool,
};

export default BindingSlotLabel;
